import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import SearchableSelect from "../../components/common/SearchableSelect";

const ROLE_TABS = [
  { key: "player", label: "Player" },
  { key: "organizer", label: "Organizer" },
  { key: "manager", label: "Event Manager" },
  { key: "tech", label: "Tech" },
  { key: "ai", label: "AI" },
];

const GameDay2 = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [date, setDate] = useState(searchParams.get("date") || new Date().toISOString().slice(0, 10));
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(searchParams.get("event_id") || "");
  const [activeTab, setActiveTab] = useState("player");
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    gender: (searchParams.get("gender") || "all").toLowerCase(),
    age_category: "all",
    status: "all",
    q: "",
    per_page: 120,
    page: 1,
  });
  const [payload, setPayload] = useState({
    event: null,
    summary: {},
    participants: [],
    game_breakdown: [],
    classification_breakdown: [],
    ai_insights: [],
    pagination: { page: 1, per_page: 120, total: 0, total_pages: 1 },
  });
  const [error, setError] = useState("");

  const selectedEvent = useMemo(
    () => events.find((e) => String(e.id) === String(eventId)) || null,
    [events, eventId]
  );

  const pendingDocsRows = useMemo(
    () => (payload.participants || []).filter((r) => Number(r.doc_missing_count || 0) > 0),
    [payload.participants]
  );

  const loadEvents = async (targetDate = date) => {
    setLoading(true);
    setError("");
    try {
      const res = await API.fetchEventsByDate(targetDate);
      const list = res?.data || res || [];
      const next = Array.isArray(list) ? list : [];
      setEvents(next);
      if (!next.find((e) => String(e.id) === String(eventId))) {
        setEventId(next[0] ? String(next[0].id) : "");
      }
    } catch (e) {
      setEvents([]);
      setEventId("");
      setError(e?.message || "Failed to load events.");
    } finally {
      setLoading(false);
    }
  };

  const loadDashboard = async () => {
    if (!eventId) {
      setPayload((prev) => ({ ...prev, participants: [], game_breakdown: [], classification_breakdown: [] }));
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await API.get("/admin/game-day/v2/dashboard", {
        params: {
          event_id: eventId,
          ...filters,
        },
      });
      const data = res?.data || res?.data?.data || res || {};
      setPayload({
        event: data?.event || null,
        summary: data?.summary || {},
        participants: Array.isArray(data?.participants) ? data.participants : [],
        game_breakdown: Array.isArray(data?.game_breakdown) ? data.game_breakdown : [],
        classification_breakdown: Array.isArray(data?.classification_breakdown)
          ? data.classification_breakdown
          : [],
        ai_insights: Array.isArray(data?.ai_insights) ? data.ai_insights : [],
        pagination: data?.pagination || { page: 1, per_page: 120, total: 0, total_pages: 1 },
      });
    } catch (e) {
      setError(e?.message || "Failed to load GameDay2 dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    const qDate = searchParams.get("date");
    const qEventId = searchParams.get("event_id");
    const qGender = (searchParams.get("gender") || "").toLowerCase();
    if (qDate && qDate !== date) setDate(qDate);
    if (qEventId && qEventId !== eventId) setEventId(qEventId);
    if (qGender && qGender !== filters.gender) {
      setFilters((prev) => ({ ...prev, gender: qGender }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const t = setTimeout(() => {
      loadDashboard();
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, filters]);

  const summaryCards = [
    { label: "Total Players", value: payload.summary?.total_players || 0, color: "text-slate-900" },
    { label: "Approved", value: payload.summary?.approved || 0, color: "text-emerald-700" },
    { label: "Pending", value: payload.summary?.pending || 0, color: "text-amber-700" },
    { label: "Rejected", value: payload.summary?.rejected || 0, color: "text-rose-700" },
    { label: "Docs Pending", value: payload.summary?.docs_pending || 0, color: "text-orange-700" },
    {
      label: "Class Pending",
      value: payload.summary?.classification_pending || 0,
      color: "text-violet-700",
    },
  ];
  const activeGenderLabel =
    filters.gender === "male" ? "Male" : filters.gender === "female" ? "Female" : "All";
  const gameDayContextQuery = useMemo(() => {
    const q = new URLSearchParams();
    if (date) q.set("date", date);
    if (eventId) q.set("event_id", String(eventId));
    q.set("gender", filters.gender);
    return q.toString();
  }, [date, eventId, filters.gender]);

  return (
    <>
      <Navbar />
      <div className="min-h-screen">
        <div className="max-w-[1400px] mx-auto p-3 sm:p-4 md:p-6 mobile-page-pad">
          <section className="mb-3 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-3 sm:p-3.5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">Command Center</p>
                <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-0.5">Game Day 2</h1>
              </div>
              <button
                onClick={() => navigate(`/admin/game-day?${gameDayContextQuery}`)}
                className="px-3 py-1.5 text-xs sm:text-sm rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"
              >
                Open Classic Game Day
              </button>
            </div>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Event</p>
                <p className="text-sm font-semibold text-slate-900 truncate">{selectedEvent?.title || "None"}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Gender</p>
                <p className="text-base font-semibold text-slate-900">{activeGenderLabel}</p>
              </div>
              <div className="rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-cyan-700">Participants</p>
                <p className="text-base font-semibold text-cyan-800">{payload.pagination?.total || 0}</p>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-amber-700">AI Insights</p>
                <p className="text-base font-semibold text-amber-800">{payload.ai_insights.length}</p>
              </div>
            </div>
          </section>

          <div className="grid gap-4 xl:grid-cols-[310px_minmax(0,1fr)]">
            <aside className="self-start rounded-3xl border border-slate-200 bg-white p-4 shadow-sm xl:sticky xl:top-24">
              <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-600">Filters</h2>
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Date</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full border border-slate-300 rounded-xl px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Event</label>
                  <SearchableSelect
                    value={eventId}
                    onChange={(e) => setEventId(e.target.value)}
                    className="w-full border border-slate-300 rounded-xl px-3 py-2"
                  >
                    <option value="">-- Select Event --</option>
                    {events.map((e) => (
                      <option key={e.id} value={e.id}>{e.title || `Event #${e.id}`}</option>
                    ))}
                  </SearchableSelect>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Gender</label>
                  <SearchableSelect
                    value={filters.gender}
                    onChange={(e) => setFilters((prev) => ({ ...prev, gender: e.target.value, page: 1 }))}
                    className="w-full border border-slate-300 rounded-xl px-3 py-2"
                  >
                    <option value="all">All</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </SearchableSelect>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Age Group</label>
                  <SearchableSelect
                    value={filters.age_category}
                    onChange={(e) => setFilters((prev) => ({ ...prev, age_category: e.target.value, page: 1 }))}
                    className="w-full border border-slate-300 rounded-xl px-3 py-2"
                  >
                    <option value="all">All</option>
                    <option value="sub_junior">Sub-Junior</option>
                    <option value="junior">Junior</option>
                    <option value="senior">Senior</option>
                  </SearchableSelect>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Approval Status</label>
                  <SearchableSelect
                    value={filters.status}
                    onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value, page: 1 }))}
                    className="w-full border border-slate-300 rounded-xl px-3 py-2"
                  >
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </SearchableSelect>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Search Player / District / Reg No / Bib No</label>
                  <input
                    type="text"
                    value={filters.q}
                    onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value, page: 1 }))}
                    className="w-full border border-slate-300 rounded-xl px-3 py-2"
                    placeholder="Type player, district, reg no..."
                  />
                </div>
                <button
                  onClick={() => loadEvents(date)}
                  className="w-full h-11 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700"
                >
                  {loading ? "Loading..." : "Refresh"}
                </button>
                {error ? <p className="text-sm text-rose-700">{error}</p> : null}
              </div>

              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Current Event</p>
                <p className="text-sm font-semibold text-slate-900 mt-1">{selectedEvent?.title || "No event selected"}</p>
                <p className="text-xs text-slate-600 mt-1">{selectedEvent?.location || "Location not set"}</p>
              </div>
              <p className="text-xs text-blue-700 mt-3">Active Filter: Gender = {activeGenderLabel}</p>
            </aside>

            <div className="space-y-4">
              <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                  {summaryCards.map((c) => (
                    <div key={c.label} className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-3">
                      <p className="text-xs text-slate-500">{c.label}</p>
                      <p className={`text-2xl font-bold ${c.color}`}>{c.value}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap gap-2">
                  {ROLE_TABS.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setActiveTab(t.key)}
                      className={`px-4 py-2 rounded-xl text-sm font-semibold ${
                        activeTab === t.key ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </section>

              {activeTab === "player" ? (
                <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                  <h2 className="font-semibold text-slate-900 mb-3">Player View</h2>
                  <div className="overflow-x-auto rounded-2xl border border-slate-200">
                    <table className="min-w-full text-sm border-0">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="border p-2">Player</th>
                          <th className="border p-2">Gender</th>
                          <th className="border p-2">Age Group</th>
                          <th className="border p-2">District</th>
                          <th className="border p-2">Reg / Bib</th>
                          <th className="border p-2">Classification</th>
                          <th className="border p-2">Games</th>
                          <th className="border p-2">Docs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payload.participants.map((r) => (
                          <tr key={r.registration_id}>
                            <td className="border p-2">{r.player_name}</td>
                            <td className="border p-2">{r.gender || "-"}</td>
                            <td className="border p-2">{r.age_category || "-"}</td>
                            <td className="border p-2">{r.district || "-"}</td>
                            <td className="border p-2">{(r.reg_no || "-") + " / " + (r.bib_no || "-")}</td>
                            <td className="border p-2">{r.classification || "Unclassified"}</td>
                            <td className="border p-2">{r.games || "-"}</td>
                            <td className="border p-2">
                              {r.docs_status === "Complete" ? (
                                <span className="text-emerald-700 font-medium">Complete</span>
                              ) : (
                                <span className="text-amber-700 font-medium">Pending ({r.doc_missing_count})</span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {!payload.participants.length ? (
                          <tr><td className="border p-3 text-center text-slate-500" colSpan={8}>No players found.</td></tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : null}

              {activeTab === "organizer" ? (
                <section className="grid lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-2 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                    <h2 className="font-semibold text-slate-900 mb-3">Verification Queue (Docs Pending)</h2>
                    <div className="overflow-x-auto rounded-2xl border border-slate-200">
                      <table className="min-w-full text-sm border-0">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="border p-2">Player</th>
                            <th className="border p-2">District</th>
                            <th className="border p-2">Missing Docs</th>
                            <th className="border p-2">Games</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendingDocsRows.map((r) => (
                            <tr key={r.registration_id}>
                              <td className="border p-2">{r.player_name}</td>
                              <td className="border p-2">{r.district || "-"}</td>
                              <td className="border p-2 text-amber-700">{(r.missing_docs || []).join(", ") || "-"}</td>
                              <td className="border p-2">{r.games || "-"}</td>
                            </tr>
                          ))}
                          {!pendingDocsRows.length ? (
                            <tr><td className="border p-3 text-center text-slate-500" colSpan={4}>No pending document rows.</td></tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                    <h3 className="font-semibold text-slate-900 mb-3">Organizer Actions</h3>
                    <div className="space-y-2">
                      <button
                        className="w-full h-10 rounded-lg bg-indigo-600 text-white font-semibold"
                        onClick={() => navigate(`/admin/event-registrations?event_id=${eventId}`)}
                        disabled={!eventId}
                      >
                        Open Event Approvals
                      </button>
                      <button
                        className="w-full h-10 rounded-lg bg-slate-700 text-white font-semibold"
                        onClick={() => navigate(`/admin/game-day?${gameDayContextQuery}`)}
                      >
                        Download Game Day PDFs
                      </button>
                      <button
                        className="w-full h-10 rounded-lg bg-violet-700 text-white font-semibold"
                        onClick={() => navigate(`/admin/classification-allotment`)}
                      >
                        Classification Desk
                      </button>
                    </div>
                  </div>
                </section>
              ) : null}

              {activeTab === "manager" ? (
                <section className="grid lg:grid-cols-2 gap-4">
                  <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                    <h2 className="font-semibold text-slate-900 mb-3">Game-wise Load</h2>
                    <div className="overflow-x-auto rounded-2xl border border-slate-200">
                      <table className="min-w-full text-sm border-0">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="border p-2">Game</th>
                            <th className="border p-2">Participants</th>
                            <th className="border p-2">Approved</th>
                            <th className="border p-2">Docs Pending</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payload.game_breakdown.map((g) => (
                            <tr key={`${g.game_id}_${g.game_name}`}>
                              <td className="border p-2">{g.game_name}</td>
                              <td className="border p-2">{g.participants}</td>
                              <td className="border p-2 text-emerald-700">{g.approved}</td>
                              <td className="border p-2 text-amber-700">{g.pending_docs}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                    <h2 className="font-semibold text-slate-900 mb-3">Category-wise Distribution</h2>
                    <div className="overflow-x-auto rounded-2xl border border-slate-200">
                      <table className="min-w-full text-sm border-0">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="border p-2">Classification</th>
                            <th className="border p-2">Participants</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payload.classification_breakdown.map((c) => (
                            <tr key={c.classification}>
                              <td className="border p-2">{c.classification}</td>
                              <td className="border p-2">{c.participants}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </section>
              ) : null}

              {activeTab === "tech" ? (
                <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                  <h2 className="font-semibold text-slate-900 mb-3">Tech Operations</h2>
                  <div className="grid md:grid-cols-2 gap-3">
                    <button
                      className="px-4 py-3 rounded-xl bg-blue-600 text-white text-left"
                      onClick={() => navigate(`/admin/game-engine?${gameDayContextQuery}`)}
                      disabled={!eventId}
                    >
                      <div className="font-semibold">Heat/Fixture Control</div>
                      <div className="text-xs">Generate, load manual, and save schedules.</div>
                    </button>
                    <button
                      className="px-4 py-3 rounded-xl bg-emerald-600 text-white text-left"
                      onClick={() => navigate(`/admin/schedule/${eventId}?${gameDayContextQuery}`)}
                      disabled={!eventId}
                    >
                      <div className="font-semibold">Schedule Board</div>
                      <div className="text-xs">Auto schedule and track live operations.</div>
                    </button>
                    <button
                      className="px-4 py-3 rounded-xl bg-violet-600 text-white text-left"
                      onClick={() => navigate(`/admin/results/timed/${eventId}?${gameDayContextQuery}`)}
                      disabled={!eventId}
                    >
                      <div className="font-semibold">Timed Results</div>
                      <div className="text-xs">Capture race times with quick flow.</div>
                    </button>
                    <button
                      className="px-4 py-3 rounded-xl bg-amber-600 text-white text-left"
                      onClick={() => navigate(`/admin/results/points/${eventId}?${gameDayContextQuery}`)}
                      disabled={!eventId}
                    >
                      <div className="font-semibold">Points Results</div>
                      <div className="text-xs">Capture attempts and best marks.</div>
                    </button>
                  </div>
                </section>
              ) : null}

              {activeTab === "ai" ? (
                <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                  <h2 className="font-semibold text-slate-900 mb-3">AI Insights</h2>
                  <div className="grid md:grid-cols-2 gap-3">
                    {payload.ai_insights.map((ins, idx) => (
                      <div key={`${ins.title}_${idx}`} className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4">
                        <p className="text-xs uppercase tracking-wide text-slate-500">{ins.severity}</p>
                        <h3 className="font-semibold text-slate-900 mt-1">{ins.title}</h3>
                        <p className="text-sm text-slate-600 mt-2">{ins.message}</p>
                      </div>
                    ))}
                    {!payload.ai_insights.length ? (
                      <p className="text-sm text-slate-500">No AI insights available for current filters.</p>
                    ) : null}
                  </div>
                </section>
              ) : null}

              <div className="text-xs text-slate-500">
                Showing {payload.participants.length} rows | Page {payload.pagination?.page || 1} of {payload.pagination?.total_pages || 1} | Total {payload.pagination?.total || 0}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GameDay2;
